import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Martini, UtensilsCrossed, ArrowRight } from "lucide-react";

const BG_URL = "/pozaMenuPage.jpeg";

const BARRELS_LOGO_URL = "/LogoBarrels.png";

// Drinks = the bar's own menu (the PDF that opens today). Same tab so the
// browser back button returns to this choice page.
const DRINKS_URL = "/meniu-sorriso-2026.pdf?v=4";

// Food = external ordering via Foodstation (delivered by Barrells Pub).
const FOOD_URL = "https://beachdelivery2.foodstation.ro/c/6-beach/";

const STORAGE_KEY = "sorriso_lang";

const I18N = {
  ro: {
    back: "Înapoi",
    eyebrow: "Sorriso Beach",
    title: "Meniu",
    subtitle: "Alege ce dorești",
    drinksLabel: "Băuturi",
    drinksDesc: "Meniul barului Sorriso",
    foodLabel: "Mâncare",
    foodDesc: "Comandă online",
    foodNote: "Mâncarea se livrează de la Barrels Pub",
  },
  en: {
    back: "Back",
    eyebrow: "Sorriso Beach",
    title: "Menu",
    subtitle: "Choose what you'd like",
    drinksLabel: "Drinks",
    drinksDesc: "Sorriso bar menu",
    foodLabel: "Food",
    foodDesc: "Order online",
    foodNote: "Food is delivered by Barrels Pub",
  },
};

export default function MenuChoice() {
  const [lang] = useState(() => {
    if (typeof window === "undefined") return "ro";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "ro" || saved === "en") return saved;
    const nav = (navigator.language || "ro").toLowerCase();
    return nav.startsWith("ro") ? "ro" : "en";
  });

  const t = I18N[lang];

  return (
    <div
      className="relative flex min-h-[100svh] w-full flex-col overflow-hidden bg-black text-white"
      data-testid="menu-choice-page"
      style={{ fontFamily: "'TeX Gyre Adventor', 'Tex Gyre Adventor', sans-serif" }}
    >
      {/* Blurred background photo */}
      <div
        aria-hidden="true"
        className="absolute inset-0 scale-110 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_URL})`, filter: "blur(14px)" }}
        data-testid="menu-choice-bg"
      />
      {/* Dark overlay for contrast */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/70"
      />

      {/* Content */}
      <div className="relative z-10 mx-auto flex w-full max-w-md flex-1 flex-col px-5 pb-7 pt-5">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <Link
            to="/"
            data-testid="menu-choice-back"
            className="group flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-[13px] font-semibold text-white backdrop-blur-md transition-all hover:bg-white/20 active:scale-[0.97]"
          >
            <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" strokeWidth={2} />
            {t.back}
          </Link>
          <span className="text-[11px] uppercase tracking-[0.25em] text-white/70">
            {t.eyebrow}
          </span>
        </div>

        {/* Title */}
        <div className="mt-7">
          <h1
            className="text-white"
            style={{ fontSize: "clamp(40px, 13vw, 60px)", lineHeight: 1, letterSpacing: "-0.02em", fontWeight: 700 }}
            data-testid="menu-choice-title"
          >
            {t.title}
          </h1>
          <p className="mt-2 text-[13px] uppercase tracking-[0.28em] text-white/65">
            {t.subtitle}
          </p>
        </div>

        {/* Two big liquid-glass buttons */}
        <div className="mt-7 flex flex-1 flex-col gap-4">
          {/* Drinks */}
          <a
            href={DRINKS_URL}
            data-testid="menu-choice-drinks"
            className="group relative flex flex-1 flex-col justify-between overflow-hidden rounded-[30px] border border-white/25 bg-gradient-to-br from-white/25 to-white/5 p-6 shadow-[0_20px_60px_-18px_rgba(0,0,0,0.7)] ring-1 ring-inset ring-white/10 backdrop-blur-2xl transition-all hover:from-white/30 hover:to-white/10 active:scale-[0.98]"
          >
            <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent" />
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 ring-1 ring-white/40">
              <Martini className="h-7 w-7" strokeWidth={1.8} />
            </span>
            <span className="mt-4 flex items-end justify-between">
              <span className="flex flex-col">
                <span className="text-[26px] font-semibold leading-none">{t.drinksLabel}</span>
                <span className="mt-1.5 text-[13px] text-white/70">{t.drinksDesc}</span>
              </span>
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/30 transition-transform group-hover:translate-x-0.5">
                <ArrowRight className="h-5 w-5" strokeWidth={2} />
              </span>
            </span>
          </a>

          {/* Food + note */}
          <div className="flex flex-1 flex-col gap-2.5">
            <a
              href={FOOD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="menu-choice-food"
              className="group relative flex flex-1 flex-col justify-between overflow-hidden rounded-[30px] border border-white/25 bg-gradient-to-br from-white/25 to-white/5 p-6 shadow-[0_20px_60px_-18px_rgba(0,0,0,0.7)] ring-1 ring-inset ring-white/10 backdrop-blur-2xl transition-all hover:from-white/30 hover:to-white/10 active:scale-[0.98]"
            >
              <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent" />
              {/* Barrells logo across the whole button (watermark behind the content) */}
              <img
                src={BARRELS_LOGO_URL}
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[82%] w-[82%] -translate-x-1/2 -translate-y-1/2 object-contain opacity-55"
              />
              <span className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#e8a13a]/90 text-black ring-1 ring-white/30">
                <UtensilsCrossed className="h-7 w-7" strokeWidth={1.8} />
              </span>
              <span className="relative z-10 mt-4 flex items-end justify-between">
                <span className="flex flex-col">
                  <span className="text-[26px] font-semibold leading-none">{t.foodLabel}</span>
                  <span className="mt-1.5 text-[13px] text-white/70">{t.foodDesc}</span>
                </span>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/30 transition-transform group-hover:translate-x-0.5">
                  <ArrowRight className="h-5 w-5" strokeWidth={2} />
                </span>
              </span>
            </a>

            {/* Delivery note, directly under the food button */}
            <p
              className="text-center text-[12px] tracking-wide text-white/75"
              data-testid="menu-choice-food-note"
            >
              {t.foodNote}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
